// create a mutation
import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const createPaymentServiceAccountRequestSchema = z.object({
  agentId: z.string(),
  email: z.string().optional(),
  msa: z.string().optional(),
  individual: z
    .object({
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      phone: z.string().optional(),
      birthday: z.string().optional(),
      address: z
        .object({
          line1: z.string().optional(),
          postalCode: z.string().optional(),
          city: z.string().optional(),
          state: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
});

const createPaymentServiceAccountResponseSchema = z.object({
  _id: z.string(),
  accountId: z.string(),
  createdAt: z.string(),
  enabled: z.boolean(),
  updatedAt: z.string(),
  status: z.string(),
});

const url = `${environmentConfig.REACT_APP_PAYMENTS_API_URL}/accounts`;

export type CreatePaymentServiceAccountRequest = z.infer<
  typeof createPaymentServiceAccountRequestSchema
>;

export type CreatePaymentServiceAccountResponse = z.infer<
  typeof createPaymentServiceAccountResponseSchema
>;
export function useCreatePaymentServiceAccount(
  options: UseMutationOptions<
    CreatePaymentServiceAccountResponse,
    AxiosError,
    CreatePaymentServiceAccountRequest
  > = {}
) {
  const { refetch: refetchWorker } = useGetWorker({
    enabled: false,
  });

  return useMutation({
    mutationKey: ["create-payment-service-account"],
    mutationFn: async (data: CreatePaymentServiceAccountRequest) => {
      const response = await post({
        url,
        data,
        requestSchema: createPaymentServiceAccountRequestSchema,
        responseSchema: createPaymentServiceAccountResponseSchema,
      });

      // To update worker cache with the new payment service account info
      await refetchWorker();

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.CREATE_PAYMENT_SERVICE_ACCOUNT_FAILURE,
    },
    ...options,
  });
}
